import * as React from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Link } from '@factorialco/f0-react'
import { RichTextDisplay } from '@factorialco/f0-react/dist/experimental'
import {
  CompetencyFragment,
  LearningPlatformOverviewDocument,
  TrainingDetailFragment,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import i18n from 'modules/core/lib/i18n'

import { FormsScores } from 'modules/forms'
import { OverviewTag } from 'modules/trainings/components/Revamp/TrainingsDetail/Overview/OverviewTag'
import Syllabus from 'modules/trainings/components/content/Syllabus'
import { DetailOutletContext } from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/types'

import { textDisplay } from './index.vanilla.css'

const MainContent = ({
  training,
  competencies,
}: {
  training: TrainingDetailFragment
  competencies: CompetencyFragment[] | []
}) => {
  const { description, id: trainingId, validFor, objectives } = training
  const { data } = useQuery(LearningPlatformOverviewDocument, {
    variables: { trainingId },
  })
  const { hasContentFF } = useOutletContext<DetailOutletContext>()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { loading: richTextEditorLoading } = useRichTextEditor()
  const learningPlatform = data?.trainings.learningPlatformsConnection.nodes[0]
  if (richTextEditorLoading) return null

  return (
    <Box style={{ flex: '2' }} gap="s24">
      <FormsScores
        resourceId={String(training.id)}
        resourceType="Trainings::Training"
        scores={{
          coursesatisfaction: i18n.t('trainings.trainings_detail.scores.titles.satisfaction'),
          courseeffectiveness: i18n.t('trainings.trainings_detail.scores.titles.effectiveness'),
          courseknowledge: i18n.t('trainings.trainings_detail.scores.titles.knowledge'),
        }}
      />
      <Box gap="s8">
        <Text.Normal size="300" weight="semibold">
          {i18n.t('trainings.trainings_revamp.training_detail.overview.competencies')}
        </Text.Normal>
        <Box gap="s8" flexDirection="row" flexWrap="wrap">
          {competencies && competencies.length
            ? competencies.map((competency) => (
                <OverviewTag key={competency.id} tagName={competency.name} />
              ))
            : '-'}
        </Box>
      </Box>
      <Box gap="s8">
        <Text.Normal weight="semibold">
          {i18n.t('trainings.trainings_revamp.training_detail.overview.objectives')}
        </Text.Normal>
        <Box className={textDisplay}>
          <RichTextDisplay content={objectives || ''} />
        </Box>
      </Box>
      <Box gap="s8">
        <Text.Normal weight="semibold">
          {i18n.t('trainings.trainings_revamp.training_detail.overview.description')}
        </Text.Normal>
        <Box className={textDisplay}>
          <RichTextDisplay content={description} />
        </Box>
      </Box>
      {validFor && (
        <Box gap="s8">
          <Text.Normal size="300" weight="semibold">
            {i18n.t('trainings.trainings_revamp.training_detail.overview.course_validity')}
          </Text.Normal>
          <Text.Normal>
            {i18n.tp(
              'trainings.trainings_revamp.new_training.wizard.basic_information.course_validity.year_info',
              { count: validFor }
            )}
          </Text.Normal>
        </Box>
      )}
      {learningPlatform?.link && (
        <Box gap="s8">
          <Text.Normal size="300" weight="semibold" className="text-f1-foreground">
            {i18n.t('trainings.my_trainings_revamp.training_detail.overview.learning_platform')}
          </Text.Normal>
          <F0Link href={learningPlatform?.link}>{learningPlatform?.link}</F0Link>
        </Box>
      )}
      {hasContentFF && (
        <Box gap="s8">
          <Text.Normal size="300" weight="semibold">
            {i18n.t('trainings.trainings_revamp.training_detail.overview.course_content')}
          </Text.Normal>
          <Syllabus trainingId={training.id} />
        </Box>
      )}
    </Box>
  )
}

export default MainContent
